import { useEffect, useRef, useState } from "react"
import { gsap } from "gsap"
import { Play, Plus, Info, ChevronLeft, ChevronRight, Star } from "lucide-react"
import { Button } from "./ui/button"
import { Badge } from "./ui/badge"
import { cursosAPI } from "../services/api"
import { useNavigate } from "react-router-dom"

const API_BASE_URL = 'http://localhost:5000'

export function HeroSection() {
  const heroRef = useRef(null)
  const navigate = useNavigate()
  const [cursosPopulares, setCursosPopulares] = useState([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const [isAutoPlaying, setIsAutoPlaying] = useState(true)
  const autoPlayRef = useRef(null)

  // Buscar cursos populares
  useEffect(() => {
    const buscarCursosPopulares = async () => {
      try {
        setLoading(true)
        
        // Buscar cursos populares da nova API pública
        const response = await cursosAPI.populares()
        const cursosPopulares = response.data || []
        
        if (cursosPopulares.length > 0) {
          setCursosPopulares(cursosPopulares)
        } else {
          // Fallback: buscar todos os cursos se não houver populares
          const cursosResponse = await cursosAPI.listar()
          const cursos = cursosResponse.data || []
          
          // Ordenar por total de alunos (se disponível) e pegar os primeiros 5
          const cursosOrdenados = cursos
            .sort((a, b) => (b.total_alunos || 0) - (a.total_alunos || 0))
            .slice(0, 5)
          
          setCursosPopulares(cursosOrdenados)
        }
      } catch (error) {
        console.error("Erro ao carregar cursos populares:", error)
        
        try {
          // Fallback: buscar todos os cursos
          const cursosResponse = await cursosAPI.listar()
          const cursos = cursosResponse.data || []
          
          const cursosOrdenados = cursos
            .sort((a, b) => (b.total_alunos || 0) - (a.total_alunos || 0))
            .slice(0, 5)
          
          setCursosPopulares(cursosOrdenados)
        } catch (fallbackError) {
          console.error("Erro no fallback:", fallbackError)
          // Último fallback com curso padrão
          setCursosPopulares([{
            id: 1,
            titulo: "React Avançado",
            descricao: "Domine React com hooks avançados, Context API, performance optimization e as melhores práticas do mercado.",
            categoria_nome: "Programação",
            total_alunos: 2847,
            duracao: "15h"
          }])
        }
      } finally {
        setLoading(false)
      }
    }

    buscarCursosPopulares()
  }, [])

  // Auto-play do carrossel
  useEffect(() => {
    if (isAutoPlaying && cursosPopulares.length > 1) {
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => 
          prevIndex === cursosPopulares.length - 1 ? 0 : prevIndex + 1
        )
      }, 5000) // Muda a cada 5 segundos
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current)
      }
    }
  }, [isAutoPlaying, cursosPopulares.length, currentIndex])

  // Animações GSAP
  useEffect(() => {
    if (loading || cursosPopulares.length === 0) return

    const ctx = gsap.context(() => {
      // Verificar e animar background
      const heroBgElements = heroRef.current?.querySelectorAll(".hero-bg")
      if (heroBgElements && heroBgElements.length > 0) {
        gsap.fromTo(heroBgElements, { scale: 1.1, opacity: 0 }, { scale: 1, opacity: 1, duration: 1.5, ease: "power2.out" })
      }

      // Verificar e animar conteúdo
      const heroContentElements = heroRef.current?.querySelectorAll(".hero-content > *")
      if (heroContentElements && heroContentElements.length > 0) {
        gsap.fromTo(
          heroContentElements,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.1,
            delay: 0.3,
            ease: "power2.out",
          },
        )
      }

      // Animação de hover nos botões
      const buttons = heroRef.current?.querySelectorAll(".hero-button")
      buttons?.forEach((button) => {
        button.addEventListener("mouseenter", () => {
          gsap.to(button, { scale: 1.05, duration: 0.2 })
        })
        button.addEventListener("mouseleave", () => {
          gsap.to(button, { scale: 1, duration: 0.2 })
        })
      })
    }, heroRef)

    return () => ctx.revert()
  }, [loading, cursosPopulares])

  // Transição entre cursos
  useEffect(() => {
    if (cursosPopulares.length > 0) {
      const cursoContentElements = document.querySelectorAll(".curso-content")
      if (cursoContentElements && cursoContentElements.length > 0) {
        gsap.fromTo(
          cursoContentElements,
          { opacity: 0, x: 50 },
          { opacity: 1, x: 0, duration: 0.6, ease: "power2.out" }
        )
      }
    }
  }, [currentIndex, cursosPopulares])

  const proximoCurso = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === cursosPopulares.length - 1 ? 0 : prevIndex + 1
    )
  }

  const cursoAnterior = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? cursosPopulares.length - 1 : prevIndex - 1
    )
  }

  const irParaCurso = (index) => {
    setCurrentIndex(index)
  }

  const handleAssistirAgora = () => {
    const cursoAtual = cursosPopulares[currentIndex]
    if (cursoAtual?.id) {
      navigate(`/curso/${cursoAtual.id}`)
    }
  }

  const handleMaisInformacoes = () => {
    const cursoAtual = cursosPopulares[currentIndex]
    if (cursoAtual?.id) {
      navigate(`/curso/${cursoAtual.id}`)
    }
  }

  if (loading) {
    return (
      <section className="relative h-[70vh] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-900/50 to-transparent z-10" />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
        <div className="relative z-20 h-full flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 border-4 border-slate-700 border-t-red-600 rounded-full animate-spin mx-auto"></div>
            <p className="text-white text-lg">Carregando cursos em destaque...</p>
          </div>
        </div>
      </section>
    )
  }

  if (cursosPopulares.length === 0) {
    return null
  }

  const cursoAtual = cursosPopulares[currentIndex]

  return (
    <section 
      ref={heroRef} 
      className="relative h-[70vh] overflow-hidden fade-in"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      {/* Background com gradiente */}
      <div className="hero-bg absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-900/50 to-transparent z-10" />

      {/* Background image dinâmico baseado no curso atual */}
      <div
        className="hero-bg absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-1000"
        style={{
          backgroundImage: cursoAtual.capa 
            ? `url(${API_BASE_URL}/uploads/${cursoAtual.capa})` 
            : `linear-gradient(135deg, #1e293b 0%, #0f172a 50%), url(${API_BASE_URL}/uploads/fundo_quato.png)`,
          filter: "brightness(0.4) contrast(1.2)",
        }}
      />

      {/* Controles de navegação */}
      {cursosPopulares.length > 1 && (
        <>
          <button
            onClick={cursoAnterior}
            className="absolute left-6 top-1/2 -translate-y-1/2 z-30 bg-black/30 hover:bg-black/60 text-white p-3 rounded-full transition-all duration-200 backdrop-blur-sm"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={proximoCurso}
            className="absolute right-6 top-1/2 -translate-y-1/2 z-30 bg-black/30 hover:bg-black/60 text-white p-3 rounded-full transition-all duration-200 backdrop-blur-sm"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Conteúdo principal */}
      <div className="relative z-20 h-full flex items-center">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-2xl hero-content curso-content">
            <div className="flex items-center gap-2 mb-4">
              <Badge className="bg-red-600/20 text-red-400 border-red-600/30 hover:bg-red-600/30">
                Curso em Destaque
              </Badge>
              {cursoAtual.categoria_nome && (
                <Badge variant="outline" className="border-slate-600 text-slate-300">
                  {cursoAtual.categoria_nome}
                </Badge>
              )}
            </div>

            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-4 leading-tight">
              {cursoAtual.titulo}
              {cursoAtual.subtitulo && (
                <span className="block text-2xl lg:text-3xl text-slate-300 font-normal mt-2">
                  {cursoAtual.subtitulo}
                </span>
              )}
            </h1>

            <p className="text-lg text-slate-300 mb-6 leading-relaxed">
              {cursoAtual.descricao || "Um curso completo e atualizado para você dominar esta tecnologia."}
            </p>

            <div className="flex items-center gap-4 mb-8">
              <div className="flex text-yellow-400">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-current" />
                ))}
              </div>
              <span className="text-slate-300">
                4.9 • {cursoAtual.total_alunos || 0} {cursoAtual.total_alunos === 1 ? 'aluno' : 'alunos'}
              </span>
              {cursoAtual.duracao && (
                <span className="text-slate-300">• {cursoAtual.duracao}</span>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                onClick={handleAssistirAgora}
                className="hero-button bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold px-8 py-3 rounded-md transition-all duration-200"
              >
                <Play className="w-5 h-5 mr-2" />
                Assistir Agora
              </Button>

              <Button
                size="lg"
                variant="outline"
                className="hero-button border-slate-600 text-white hover:bg-slate-800 font-semibold px-8 py-3 rounded-md transition-all duration-200 bg-transparent"
              >
                <Plus className="w-5 h-5 mr-2" />
                Adicionar à Lista
              </Button>

              <Button
                size="lg"
                variant="ghost"
                onClick={handleMaisInformacoes}
                className="hero-button text-slate-300 hover:text-white hover:bg-slate-800/50 font-semibold px-8 py-3 rounded-md transition-all duration-200"
              >
                <Info className="w-5 h-5 mr-2" />
                Mais Informações
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Indicadores de posição */}
      {cursosPopulares.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex gap-2">
          {cursosPopulares.map((_, index) => (
            <button
              key={index}
              onClick={() => irParaCurso(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'bg-white w-8' 
                  : 'bg-white/40 hover:bg-white/70'
              }`}
            />
          ))}
        </div>
      )}
    </section>
  )
} 